import { IconFromSet } from '@gettactic/components';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { IconShape as IIconShape } from '../../../../types';
import { fetchSvgText, toDataUri } from '../../../../utils/svgAssetCache';
import { getTextProps } from '../../helpers/getTextProps';
import { useShape } from '../ShapeContext/useShape';

export const IconShapeSVG: React.FC = () => {
  const { shape } = useShape();
  const iconShape = shape as IIconShape;

  // For API-sourced icons (iconUrl), fetch the SVG and replace `currentColor`
  // with the configured color — same as the Konva renderer. Without this, the
  // browser loads the raw SVG via <image href> and has no way to resolve
  // currentColor, leaving the icon in its default color.
  const [coloredIconHref, setColoredIconHref] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    if (!iconShape.iconUrl) {
      setColoredIconHref(null);
      return;
    }
    let cancelled = false;
    // Shared session cache — one fetch per url across all icon shapes.
    fetchSvgText(iconShape.iconUrl)
      .then((svgText) => {
        if (cancelled) return;
        const coloredSvg = svgText.replace(
          /currentColor/g,
          iconShape.color || 'currentColor'
        );
        setColoredIconHref(toDataUri(coloredSvg));
      })
      .catch(() => {
        // Fall back to the raw URL if the fetch fails.
        if (!cancelled) setColoredIconHref(null);
      });
    return () => {
      cancelled = true;
    };
  }, [iconShape.iconUrl, iconShape.color]);

  const svgMarkup = React.useMemo(() => {
    // Prefer the pre-trimmed svgContent baked in at placement time — it
    // has a viewBox that hugs the actual content bbox, so the icon fills
    // the rendered shape without left/right padding. Apply currentColor
    // substitution so theming still works.
    if (iconShape.svgContent) {
      const colored = iconShape.svgContent.replace(
        /currentColor/g,
        iconShape.color || 'currentColor'
      );
      return toDataUri(colored);
    }
    if (iconShape.iconUrl) {
      return coloredIconHref ?? iconShape.iconUrl;
    }
    const markup = ReactDOMServer.renderToStaticMarkup(
      <IconFromSet icon={iconShape.icon} color={iconShape.color} size={64} />
    );
    return toDataUri(markup);
  }, [
    iconShape.icon,
    iconShape.iconUrl,
    iconShape.svgContent,
    iconShape.color,
    coloredIconHref
  ]);

  // Match Konva's transform pipeline (translate · rotate · scale) so flipped
  // and rotated shapes render at the same visual position as the editor.
  const sX = shape.scaleX || 1;
  const sY = shape.scaleY || 1;
  const rotPart = shape.rotation ? ` rotate(${shape.rotation})` : '';
  const scalePart = sX !== 1 || sY !== 1 ? ` scale(${sX}, ${sY})` : '';
  const transform = `translate(${shape.x}, ${shape.y})${rotPart}${scalePart}`;

  const textX = (() => {
    if (iconShape.textAlign === 'center') {
      return shape.width / 2;
    } else if (iconShape.textAlign === 'right') {
      return shape.width;
    } else {
      return 0;
    }
  })();

  const textAnchor = iconShape.textAlign === 'center' ? 'middle' : iconShape.textAlign === 'right' ? 'end' : 'start';
  const textProps = getTextProps(iconShape);

  return (
    <g transform={transform}>
      <image
        href={svgMarkup}
        width={shape.width}
        height={shape.height}
        preserveAspectRatio="xMidYMid meet"
      />
      {iconShape.text && (
        <text
          x={textX}
          y={iconShape.height + 10}
          style={{
            fontSize: `${textProps.fontSize}px`,
            fontFamily: textProps.fontFamily,
            fontWeight: iconShape.isBold ? '700' : '300',
            fontStyle: iconShape.isItalic ? 'italic' : 'normal',
            textDecoration: iconShape.isUnderline ? 'underline' : 'none',
            fill: iconShape.textColor || '#B5B5B5'
          }}
          textAnchor={textAnchor}
          dominantBaseline="hanging"
        >
          {iconShape.text.split('\n').map((line, i) => (
            <tspan
              key={i}
              x={textX}
              dy={i === 0 ? 0 : '1.2em'}
              dominantBaseline="hanging"
            >
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  );
};
